<template>
  <div class="card border-0 rounded-3 mb-4 bg-white shadow-sm">
    <div class="card-header bg-light-subtle d-flex justify-content-between align-items-center py-3 px-4">
      <div class="d-flex align-items-center gap-2">
        <span class="material-symbols-outlined text-primary fs-22">smart_toy</span>
        <h5 class="mb-0 fs-15 fw-bold text-dark d-flex align-items-center gap-2">
          <span>Protokol Uji Deteksi AI (SOP RBN/SOP-RC/AI/001)</span>
          <span v-if="order?.ai_check_detail?.ai_engine" class="badge" :class="order.ai_check_detail.ai_engine === 'scispace' ? 'bg-info-subtle text-info border border-info' : 'bg-primary-subtle text-primary border border-primary'">
            Engine: {{ order.ai_check_detail.ai_engine === 'scispace' ? 'SciSpace' : 'Drillbit' }}
          </span>
        </h5>
      </div>
      <span v-if="protocol" class="badge bg-success-subtle text-success fs-12 px-3 py-2">
        {{ protocol.protocol_number }}
      </span>
      <span v-else class="badge bg-warning-subtle text-warning fs-12 px-3 py-2">
        Belum Dibuat
      </span>
    </div>

    <div class="card-body p-4">
      <!-- Existing Protocol Overview -->
      <div v-if="protocol" class="mb-4 p-3 rounded-3 bg-light border border-light-subtle">
        <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <div>
            <div class="text-muted fs-12">Nomor Protokol Resmi</div>
            <h5 class="fw-bold text-primary mb-0">{{ protocol.protocol_number }}</h5>
          </div>
          <div class="d-flex gap-2">
            <button @click="generatePdf" :disabled="generatingPdf" class="btn btn-sm btn-outline-primary d-flex align-items-center gap-1">
              <span v-if="generatingPdf" class="spinner-border spinner-border-sm"></span>
              <span class="material-symbols-outlined fs-16">picture_as_pdf</span>
              <span>{{ generatingPdf ? 'Membuat PDF...' : 'Regenerate PDF Protokol' }}</span>
            </button>
            <a v-if="protocol?.generated_pdf || order.generated_protocol_pdf" :href="getFileUrl(protocol?.generated_pdf || order.generated_protocol_pdf)" target="_blank" class="btn btn-sm btn-success d-flex align-items-center gap-1">
              <span class="material-symbols-outlined fs-16">download</span>
              <span>Unduh Dokumen Resmi A4</span>
            </a>
            <a v-if="protocol.verification?.verification_code" :href="getVerificationUrl(protocol.verification.verification_code)" target="_blank" class="btn btn-sm btn-info text-white d-flex align-items-center gap-1">
              <span class="material-symbols-outlined fs-16">verified</span>
              <span>Cek Verifikasi Publik</span>
            </a>
          </div>
        </div>

        <div class="row g-2 fs-13">
          <div class="col-md-3">
            <span class="text-muted">Skor Deteksi AI:</span>
            <span class="fw-bold text-dark ms-1">{{ protocol.ai_score_initial }}% &rarr; {{ protocol.ai_score_final }}%</span>
          </div>
          <div class="col-md-3">
            <span class="text-muted">Zona Deteksi:</span>
            <span class="badge ms-1" :class="protocol.ai_score_final <= protocol.applied_threshold ? 'bg-success' : 'bg-warning'">
              {{ protocol.internal_zone }}
            </span>
          </div>
          <div class="col-md-3">
            <span class="text-muted">Status:</span>
            <span class="badge ms-1" :class="protocol.status_code === 'PASS' ? 'bg-success' : 'bg-danger'">{{ protocol.status_code }}</span>
          </div>
          <div class="col-md-3">
            <span class="text-muted">Total Run Siklus:</span>
            <span class="fw-bold text-dark ms-1">{{ protocol.total_runs_count }} Run</span>
          </div>
        </div>
      </div>

      <form @submit.prevent="saveProtocol">
        <!-- Detector & Language Scope -->
        <!-- Detector & Language Scope -->
        <div class="row mb-3">
          <div class="col-md-3 mb-2">
            <label class="form-label fs-13 fw-semibold">Perangkat AI Detector <span class="text-danger">*</span></label>
            <input type="text" class="form-control form-control-sm" v-model="form.detector_name" required placeholder="SciSpace AI / Drillbit" />
          </div>
          <div class="col-md-3 mb-2">
            <label class="form-label fs-13 fw-semibold">AI Engine Type</label>
            <select class="form-select form-select-sm" v-model="form.engine_type" @change="onEngineTypeChange">
              <option value="SciSpace">SciSpace</option>
              <option value="Drillbit">Drillbit</option>
              <option value="Turnitin AI">Turnitin AI</option>
              <option value="GPTZero">GPTZero</option>
            </select>
          </div>
          <div class="col-md-3 mb-2">
            <label class="form-label fs-13 fw-semibold">Versi Perangkat</label>
            <input type="text" class="form-control form-control-sm" v-model="form.detector_version" placeholder="2026 Model Release" />
          </div>
          <div class="col-md-3 mb-2">
            <label class="form-label fs-13 fw-semibold">Bahasa Naskah <span class="text-danger">*</span></label>
            <input type="text" class="form-control form-control-sm" v-model="form.document_language" required placeholder="Bahasa Indonesia / English" />
          </div>
        </div>

        <div class="row mb-3">
          <div class="col-md-6 mb-2">
            <label class="form-label fs-13 fw-semibold">Lingkup Teks Dianalisis</label>
            <input type="text" class="form-control form-control-sm" v-model="form.analyzed_scope" required placeholder="Seluruh bab naskah utama (abstrak s.d. kesimpulan)" />
          </div>
          <div class="col-md-6 mb-2">
            <label class="form-label fs-13 fw-semibold">Lingkup Dikecualikan</label>
            <input type="text" class="form-control form-control-sm" v-model="form.excluded_scope" placeholder="Daftar pustaka, lampiran, ucapan terima kasih" />
          </div>
        </div>

        <!-- Ambang Batas, Skor & Zona -->
        <div class="p-3 bg-light rounded-2 mb-3 border">
          <div class="fw-bold fs-13 mb-2 text-dark">Ambang Batas &amp; Pengukuran Skor AI (SOP RBN)</div>
          <div class="row">
            <div class="col-md-3 mb-2">
              <label class="form-label fs-12">Ambang Batas Maks. (%)</label>
              <input type="number" step="0.1" class="form-control form-control-sm" v-model="form.applied_threshold" @input="updateZone" required />
            </div>
            <div class="col-md-3 mb-2">
              <label class="form-label fs-12">Skor AI Awal / Run 1 (%) <span class="text-danger">*</span></label>
              <input type="number" step="0.1" class="form-control form-control-sm" v-model="form.ai_score_initial" required />
            </div>
            <div class="col-md-3 mb-2">
              <label class="form-label fs-12">Skor AI Akhir (%) <span class="text-danger">*</span></label>
              <input type="number" step="0.1" class="form-control form-control-sm" v-model="form.ai_score_final" @input="updateZone" required />
            </div>
            <div class="col-md-3 mb-2">
              <label class="form-label fs-12">Zona Internal RBN</label>
              <select class="form-select form-select-sm" v-model="form.internal_zone" required>
                <option value="Rendah (0-20%)">Rendah (0-20%)</option>
                <option value="Dalam Batas (21-40%)">Dalam Batas (21-40%)</option>
                <option value="Melebihi Batas (41-60%)">Melebihi Batas (41-60%)</option>
                <option value="Signifikan (>60%)">Signifikan (>60%)</option>
              </select>
            </div>
            <div class="col-md-6 mb-2">
              <label class="form-label fs-12">Status Kode Keputusan <span class="text-danger">*</span></label>
              <select class="form-select form-select-sm" v-model="form.status_code" required>
                <option value="PASS">PASS (Memenuhi Syarat)</option>
                <option value="REVISE">REVISE (Perlu Revisi Penulis/Editor)</option>
                <option value="CLARIFY">CLARIFY (Perlu Klarifikasi)</option>
                <option value="ESCALATE">ESCALATE (Eskalasi ke Head QC)</option>
              </select>
            </div>
            <div class="col-md-6 mb-2">
              <label class="form-label fs-12">Total Siklus Run</label>
              <input type="number" class="form-control form-control-sm" v-model="form.total_runs_count" min="1" />
            </div>
          </div>
        </div>

        <!-- Penilaian Klasifikasi & Pembobotan Deteksi AI (SciSpace AI Weightage) -->
        <div class="card border rounded-3 mb-3 shadow-sm">
          <div class="card-header bg-light d-flex align-items-center justify-content-between py-2 px-3">
            <div class="d-flex align-items-center gap-2">
              <span class="material-symbols-outlined text-primary fs-18">pie_chart</span>
              <span class="fw-bold fs-13 text-dark">Distribusi Bobot Deteksi AI (AI Weightage Breakdown)</span>
            </div>
          </div>
          <div class="card-body p-3">
            <!-- Overall Document Classification -->
            <div class="p-3 bg-light-subtle rounded border mb-3">
              <div class="fs-12 fw-bold text-dark mb-2 d-flex align-items-center gap-1">
                <span class="material-symbols-outlined fs-16 text-primary">verified_user</span>
                <span>Klasifikasi Dokumen (Overall Classification)</span>
              </div>
              <div class="row g-2">
                <div class="col-md-4">
                  <label class="form-label fs-12 mb-1">Label Klasifikasi Dokumen</label>
                  <input type="text" class="form-control form-control-sm fw-bold" v-model="form.classification_label" placeholder="cth: Essentially Human 13%" />
                </div>
                <div class="col-md-8">
                  <label class="form-label fs-12 mb-1">Keterangan / Deskripsi Evaluasi</label>
                  <input type="text" class="form-control form-control-sm" v-model="form.classification_desc" placeholder="cth: Contains small portions of AI-generated text. The rest appears written by a human." />
                </div>
              </div>
            </div>

            <!-- Checkbox Toggle Rincian Pembobotan Deteksi AI -->
            <div class="card border rounded-3 mb-3 bg-light-subtle shadow-sm">
              <div class="card-body p-3 d-flex flex-wrap justify-content-between align-items-center gap-2">
                <div class="form-check d-flex align-items-center m-0">
                  <input 
                    class="form-check-input mt-0" 
                    type="checkbox" 
                    id="describeWeightageCheck" 
                    v-model="describeWeightage" 
                    @change="onDescribeWeightageChange" 
                    style="cursor: pointer; width: 1.25em; height: 1.25em;"
                  >
                  <label class="form-check-label fw-bold text-dark fs-13 ms-2 mb-0" for="describeWeightageCheck" style="cursor: pointer;">
                    Deskripsikan Rincian Pembobotan Deteksi AI (AI Weightage Levels)
                  </label>
                  <span class="text-muted fs-12 ms-2">(Centang jika ingin mengisi pembobotan Highly, Moderately, Lowly, Human. Opsional)</span>
                </div>
                <div>
                  <span v-if="describeWeightage" class="badge bg-success-subtle text-success fs-11 px-2 py-1 border border-success-subtle d-inline-flex align-items-center gap-1">
                    <span class="material-symbols-outlined fs-13">visibility</span>
                    <span>Aktif ({{ form.section_reviews.length }} Baris Bobot)</span>
                  </span>
                  <span v-else class="badge bg-secondary-subtle text-secondary fs-11 px-2 py-1 border border-secondary-subtle d-inline-flex align-items-center gap-1">
                    <span class="material-symbols-outlined fs-13">visibility_off</span>
                    <span>Default: Tidak Dijabarkan (Opsional)</span>
                  </span>
                </div>
              </div>
            </div>

            <!-- AI Weightage Breakdown Table (Hanya muncul jika dicentang Deskripsikan) -->
            <div v-if="describeWeightage">
              <div class="d-flex align-items-center justify-content-between mb-2">
                <div class="fs-12 fw-bold text-dark d-flex align-items-center gap-1">
                  <span class="material-symbols-outlined fs-16 text-primary">bar_chart</span>
                  <span>Rincian Pembobotan Deteksi AI (AI Weightage Levels)</span>
                </div>
                <div class="d-flex gap-2">
                  <button type="button" @click="applySciSpacePreset" class="btn btn-xs btn-outline-secondary d-flex align-items-center gap-1 fs-11">
                    <span class="material-symbols-outlined fs-14">auto_fix_high</span>
                    <span>Preset Standar SciSpace</span>
                  </button>
                  <button type="button" @click="addSectionReview" class="btn btn-xs btn-primary d-flex align-items-center gap-1 fs-11">
                    <span class="material-symbols-outlined fs-14">add</span>
                    <span>Tambah Baris</span>
                  </button>
                </div>
              </div>
              <div class="table-responsive">
                <table class="table table-sm align-middle fs-12 mb-0">
                  <thead class="table-light">
                    <tr>
                      <th style="width: 28%;">Tingkat Bobot AI (Weightage Level)</th>
                      <th style="width: 16%;" class="text-center">Persentase Konten (%)</th>
                      <th style="width: 16%;" class="text-center">Jumlah Kalimat</th>
                      <th style="width: 35%;">Karakteristik / Catatan Kalimat</th>
                      <th style="width: 5%;" class="text-center">Hapus</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(sec, idx) in form.section_reviews" :key="idx">
                      <td>
                        <input type="text" class="form-control form-control-sm fw-semibold" v-model="sec.weightage_level" placeholder="cth: Highly AI written [H]" required />
                      </td>
                      <td>
                        <div class="input-group input-group-sm">
                          <input type="number" step="0.1" class="form-control form-control-sm text-center" v-model.number="sec.content_pct" placeholder="0" />
                          <span class="input-group-text px-1">%</span>
                        </div>
                      </td>
                      <td>
                        <div class="input-group input-group-sm">
                          <input type="number" class="form-control form-control-sm text-center" v-model.number="sec.sentences_count" placeholder="0" />
                          <span class="input-group-text px-1">klm</span>
                        </div>
                      </td>
                      <td>
                        <input type="text" class="form-control form-control-sm" v-model="sec.context_note" placeholder="Deskripsi karakteristik..." />
                      </td>
                      <td class="text-center">
                        <button type="button" @click="removeSectionReview(idx)" class="btn btn-sm btn-outline-danger p-1 border-0">
                          <span class="material-symbols-outlined fs-16">delete</span>
                        </button>
                      </td>
                    </tr>
                    <tr v-if="form.section_reviews.length === 0">
                      <td colspan="5" class="text-center text-muted py-3 bg-light-subtle rounded border border-dashed">
                        Belum ada rincian pembobotan. Klik <strong>"Preset Standar SciSpace"</strong> untuk memuat pembagian 4 tingkat bobot AI standar atau <strong>"Tambah Baris"</strong>.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <!-- Metadata Pengguna & Naskah -->
        <div class="row mb-3">
          <div class="col-md-4 mb-2">
            <label class="form-label fs-13 fw-semibold">Nama Pengguna / Penulis (Tampil di Protokol & PDF) <span class="text-danger">*</span></label>
            <input type="text" class="form-control form-control-sm" v-model="form.author_name_masked" required placeholder="Nama pengguna / penulis" />
          </div>
          <div class="col-md-4 mb-2">
            <label class="form-label fs-13 fw-semibold">Judul Naskah / Dokumen (Tampil di Protokol & PDF) <span class="text-danger">*</span></label>
            <input type="text" class="form-control form-control-sm" v-model="form.document_title_masked" required placeholder="Judul naskah / dokumen" />
          </div>
          <div class="col-md-4 mb-2">
            <label class="form-label fs-13 fw-semibold">Institusi / Afiliasi</label>
            <input type="text" class="form-control form-control-sm" v-model="form.institution" placeholder="Universitas ..." />
          </div>

          <!-- Examiner & Signatory Details -->
          <div class="col-12 mt-2">
            <div class="card bg-light-subtle border p-3 rounded-3 mb-2">
              <h6 class="fs-13 fw-bold text-dark mb-2 d-flex align-items-center gap-1">
                <span class="material-symbols-outlined fs-18 text-primary">draw</span>
                <span>Pemeriksa & Penandatangan Pengesahan Protokol</span>
              </h6>
              <div class="row g-3">
                <div class="col-md-4">
                  <label class="form-label fs-12 fw-semibold">Nama Examiner AI <span class="text-danger">*</span></label>
                  <input type="text" class="form-control form-control-sm" v-model="form.examiner_name" required />
                </div>
                <div class="col-md-4">
                  <label class="form-label fs-12 fw-semibold">Pilih Penandatangan Terdaftar</label>
                  <select class="form-select form-select-sm" v-model="form.signatory_id" @change="onSignatoryChange">
                    <option :value="null">-- Gunakan Penandatangan Default --</option>
                    <option v-for="sig in signatories" :key="sig.id" :value="sig.id">
                      {{ sig.name }} ({{ sig.position }}) {{ sig.is_default ? '★ Default' : '' }}
                    </option>
                  </select>
                </div>
                <div class="col-md-4">
                  <label class="form-label fs-12 fw-semibold">Nama & Jabatan Pengesahan</label>
                  <input type="text" class="form-control form-control-sm mb-1" v-model="form.signatory_name" placeholder="Nama penandatangan" />
                  <input type="text" class="form-control form-control-sm" v-model="form.signatory_position" placeholder="Jabatan penandatangan" />
                </div>
                <div v-if="selectedSignatoryPreviewUrl" class="col-12 mt-1">
                  <div class="d-flex align-items-center gap-3 p-2 bg-white rounded border">
                    <span class="fs-11 text-muted fw-semibold">Preview Tanda Tangan:</span>
                    <img :src="selectedSignatoryPreviewUrl" alt="Tanda Tangan" style="height: 32px; max-width: 120px; object-fit: contain;" />
                    <span class="fs-11 text-success d-flex align-items-center gap-1">
                      <span class="material-symbols-outlined fs-14">check_circle</span>
                      <span>Siap disematkan pada sertifikat protokol</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-12 mt-2">
            <div class="form-check">
              <input class="form-check-input" type="checkbox" v-model="form.disclaimer_agreed" id="discCheck" required>
              <label class="form-check-label fs-12 text-secondary" for="discCheck">
                Saya menyetujui Disclaimer Akademik Resmi RBN bahwa deteksi AI bersifat probabilistik dan telah diverifikasi secara manual.
              </label>
            </div>
          </div>
        </div>

        <div class="d-flex justify-content-end gap-2 mt-4">
          <button type="submit" class="btn btn-primary px-4 py-2 d-flex align-items-center gap-2" :disabled="submitting">
            <span v-if="submitting" class="spinner-border spinner-border-sm"></span>
            <span class="material-symbols-outlined fs-18">save</span>
            <span>{{ protocol ? 'Perbarui Protokol' : 'Simpan & Terbitkan Protokol RC-AI' }}</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, onMounted, computed, watch } from 'vue';
import api from '../../../services/api';
import protocolSignatoryService from '../../../services/protocolSignatoryService';

export default defineComponent({
  name: 'AiDetectorProtocolForm',
  props: {
    order: {
      type: Object,
      required: true
    }
  },
  emits: ['refresh'],
  setup(props, { emit }) {
    const protocol = computed(() => props.order?.protocol_ai_detector || null);
    const signatories = ref<any[]>([]);
    const submitting = ref(false);
    const generatingPdf = ref(false);

    const form = reactive({
      detector_name: 'SciSpace AI Detection',
      detector_version: 'v2026.1',
      engine_type: 'SciSpace',
      classification_label: 'Essentially Human 13%',
      classification_desc: 'Contains small portions of AI-generated text. The rest of the document appears to be written by a human.',
      ai_human_written_pct: null as number | null,
      ai_matched_words: null as number | null,
      ai_excluded_pct: null as number | null,
      report_submission_id: 'AI-' + Date.now(),
      document_language: 'Bahasa Indonesia',
      analyzed_scope: 'Seluruh teks artikel utama (Abstrak s.d. Kesimpulan)',
      excluded_scope: 'Daftar pustaka & lampiran',
      applied_threshold: 40,
      ai_score_initial: 20,
      ai_score_final: 13,
      total_runs_count: 1,
      status_code: 'PASS',
      internal_zone: 'Rendah (0-20%)',
      examiner_name: 'Staff Analis AI RBN',
      qc_name: 'Tim QC RBN',
      service_manager_name: 'Manajer Layanan RBN',
      director_name: 'Direktur RBN',
      signatory_id: null as number | null,
      signatory_name: '',
      signatory_position: '',
      signatory_image: null as string | null,
      disclaimer_agreed: true,
      author_name_masked: props.order?.customer?.name || 'Customer RBN',
      document_title_masked: props.order?.ai_check_detail?.source_file || ('Naskah ' + (props.order?.order_code || '')),
      institution: props.order?.customer?.university || props.order?.customer?.agency || 'Institusi Terdaftar',
      runs: [
        {
          run_number: 1,
          submitted_by_role: 'EXAMINER',
          manuscript_file: props.order?.ai_check_detail?.source_file || 'initial_file.docx',
          sha256_hash: Array.from({length: 64}, () => Math.floor(Math.random()*16).toString(16)).join(''),
          test_date: new Date().toISOString().substring(0, 10),
          score: 13,
          status: 'PASS',
          examiner_notes: 'Pengujian awal oleh tim RBN.'
        }
      ],
      section_reviews: [
        {
          weightage_level: 'Highly AI written [H]',
          content_pct: 0,
          sentences_count: 0,
          context_note: 'Pola sintaksis dan repetisi sangat kuat mencerminkan generasi mesin'
        },
        {
          weightage_level: 'Moderately AI written [M]',
          content_pct: 5,
          sentences_count: 2,
          context_note: 'Struktur kalimat terindikasi dibantu pemodelan AI'
        },
        {
          weightage_level: 'Lowly AI written [L]',
          content_pct: 8,
          sentences_count: 4,
          context_note: 'Frasa umum dengan kemungkinan kecil tumpang tindih pola bahasa'
        },
        {
          weightage_level: 'Human Written',
          content_pct: 87,
          sentences_count: 40,
          context_note: 'Struktur kalimat alami orisinal penulis dengan burstiness tinggi'
        }
      ]
    });

    function maskName(str: string): string {
      if (!str || str.length <= 2) return str;
      const parts = str.split(' ');
      return parts.map(p => p[0] + '***' + (p.length > 3 ? p[p.length - 1] : '')).join(' ');
    }

    const applySciSpacePreset = () => {
      const score = Number(form.ai_score_final) || 0;
      const high = Number((score * 0.4).toFixed(1));
      const mod = Number((score * 0.4).toFixed(1));
      const low = Number(Math.max(0, score - high - mod).toFixed(1));
      const human = Number(Math.max(0, 100 - score).toFixed(1));

      form.classification_label = score <= 20
        ? `Essentially Human ${score}%`
        : (score <= 50 ? `Partially AI Generated ${score}%` : `Predominantly AI Generated ${score}%`);
      form.classification_desc = score <= 20
        ? 'Contains small portions of AI-generated text. The rest of the document appears to be written by a human.'
        : 'Significant portions of AI-generated text detected. Revisions recommended for highlighted areas.';

      form.section_reviews = [
        {
          weightage_level: 'Highly AI written [H]',
          content_pct: high,
          sentences_count: high > 0 ? Math.ceil(high / 2) : 0,
          context_note: 'Pola sintaksis dan repetisi sangat kuat mencerminkan generasi mesin'
        },
        {
          weightage_level: 'Moderately AI written [M]',
          content_pct: mod,
          sentences_count: mod > 0 ? Math.ceil(mod / 2) : 0,
          context_note: 'Struktur kalimat terindikasi dibantu pemodelan AI'
        },
        {
          weightage_level: 'Lowly AI written [L]',
          content_pct: low,
          sentences_count: low > 0 ? Math.ceil(low / 2) : 0,
          context_note: 'Frasa umum dengan kemungkinan kecil tumpang tindih pola bahasa'
        },
        {
          weightage_level: 'Human Written',
          content_pct: human,
          sentences_count: Math.max(10, Math.round(human * 1.2)),
          context_note: 'Struktur kalimat alami orisinal penulis dengan burstiness tinggi'
        }
      ];
    };

    const onEngineTypeChange = () => {
      if (form.engine_type === 'SciSpace') {
        form.detector_name = 'SciSpace AI Detection';
      } else if (form.engine_type === 'Drillbit') {
        form.detector_name = 'Drillbit AI Detection';
      }
    };

    const updateZone = () => {
      const score = Number(form.ai_score_final);
      if (score <= 20) {
        form.internal_zone = 'Rendah (0-20%)';
        form.status_code = 'PASS';
      } else if (score <= 40) {
        form.internal_zone = 'Dalam Batas (21-40%)';
        form.status_code = 'PASS';
      } else if (score <= 60) {
        form.internal_zone = 'Melebihi Batas (41-60%)';
        form.status_code = 'REVISE';
      } else {
        form.internal_zone = 'Signifikan (>60%)';
        form.status_code = 'REVISE';
      }
      if (!form.classification_label || form.classification_label.includes('%')) {
        form.classification_label = score <= 20
          ? `Essentially Human ${score}%`
          : (score <= 50 ? `Partially AI Generated ${score}%` : `Predominantly AI Generated ${score}%`);
      }
    };

    const describeWeightage = ref(false);

    const onDescribeWeightageChange = () => {
      if (describeWeightage.value && form.section_reviews.length === 0) {
        applySciSpacePreset();
      }
    };

    const addSectionReview = () => {
      form.section_reviews.push({
        weightage_level: '',
        content_pct: 0,
        sentences_count: 0,
        context_note: ''
      });
    };

    const removeSectionReview = (idx: number) => {
      form.section_reviews.splice(idx, 1);
    };

    const saveProtocol = async (silent = false) => {
      submitting.value = true;
      try {
        const payload = {
          ...form,
          author_name: form.author_name_masked,
          document_title: form.document_title_masked,
          section_reviews: describeWeightage.value ? form.section_reviews : []
        };
        if (protocol.value) {
          await api.put(`/protocol-ai-detectors/${protocol.value.id}`, payload);
          if (!silent) alert('Protokol berhasil diperbarui.');
        } else {
          await api.post(`/orders/${props.order.order_id}/protocol-ai-detector`, payload);
          if (!silent) alert('Protokol RC-AI berhasil diterbitkan!');
        }
        emit('refresh');
        return true;
      } catch (err: any) {
        console.error('Error saving AI detector protocol:', err);
        alert(err.response?.data?.message || 'Gagal menyimpan protokol.');
        return false;
      } finally {
        submitting.value = false;
      }
    };

    const generatePdf = async () => {
      generatingPdf.value = true;
      try {
        // Auto-save form first to persist latest on-screen edits
        const saved = await saveProtocol(true);
        if (!saved) {
          return;
        }

        await api.post(`/orders/${props.order.order_id}/generate-protocol-pdf`, {
          protocol_type: 'AI_DETECTOR'
        });
        alert('PDF Protokol resmi berhasil digenerate.');
        emit('refresh');
      } catch (err: any) {
        console.error('Error generating PDF:', err);
        alert(err.response?.data?.message || 'Gagal generate PDF.');
      } finally {
        generatingPdf.value = false;
      }
    };

    const getFileUrl = (path?: string | null) => {
      if (!path) return '';
      if (path.startsWith('blob:') || path.startsWith('data:')) return path;
      let finalUrl = '';
      if (path.includes('/storage/')) {
        const cleanPath = path.replace(/^https?:\/\/[^/]+\/storage\//, '').replace(/^storage\//, '').replace(/^\//, '');
        const baseUrl = process.env.NODE_ENV === 'production'
          ? 'https://backend-beta.rayacheck.id'
          : 'http://jurnal-backend.test';
        finalUrl = `${baseUrl}/storage/${cleanPath}`;
      } else if (path.startsWith('http://') || path.startsWith('https://')) {
        finalUrl = path;
      } else {
        const cleanPath = path.replace(/^\//, '');
        const baseUrl = process.env.NODE_ENV === 'production'
          ? 'https://backend-beta.rayacheck.id'
          : 'http://jurnal-backend.test';
        finalUrl = `${baseUrl}/storage/${cleanPath}`;
      }

      if (finalUrl.toLowerCase().includes('.pdf') && !finalUrl.includes('?v=') && !finalUrl.includes('&v=') && !finalUrl.includes('?t=')) {
        finalUrl += (finalUrl.includes('?') ? '&' : '?') + 't=' + Date.now();
      }
      return finalUrl;
    };

    const getVerificationUrl = (code: string) => {
      const baseUrl = window.location.origin;
      return `${baseUrl}/verify/${code}`;
    };

    const loadSignatories = async () => {
      try {
        const res = await protocolSignatoryService.getActive();
        signatories.value = res.data.data || [];
        if (!form.signatory_id && !protocol.value?.signatory_id) {
          const def = signatories.value.find((s: any) => s.is_default) || signatories.value[0];
          if (def) {
            form.signatory_id = def.id;
            form.signatory_name = def.name;
            form.signatory_position = def.position;
            form.signatory_image = def.signature_image;
            form.director_name = def.name;
          }
        }
      } catch (err) {
        console.error('Failed to load active signatories:', err);
      }
    };

    const onSignatoryChange = () => {
      const chosen = signatories.value.find((s: any) => s.id === form.signatory_id);
      if (chosen) {
        form.signatory_name = chosen.name;
        form.signatory_position = chosen.position;
        form.signatory_image = chosen.signature_image;
        form.director_name = chosen.name;
      }
    };

    const selectedSignatoryPreviewUrl = computed(() => {
      let raw = '';
      if (form.signatory_id) {
        const chosen = signatories.value.find((s: any) => s.id === form.signatory_id);
        if (chosen) raw = chosen.signature_image_url || chosen.signature_image || '';
      } else if (protocol.value?.signatory_image_url || protocol.value?.signatory_image) {
        raw = protocol.value.signatory_image_url || protocol.value.signatory_image || '';
      } else {
        const def = signatories.value.find((s: any) => s.is_default);
        raw = def?.signature_image_url || def?.signature_image || '';
      }
      return raw ? getFileUrl(raw) : null;
    });

    onMounted(async () => {
      await loadSignatories();
      if (protocol.value) {
        Object.assign(form, protocol.value);
        if (protocol.value.verification) {
          if (protocol.value.verification.author_name_masked) form.author_name_masked = protocol.value.verification.author_name_masked;
          if (protocol.value.verification.document_title_masked) form.document_title_masked = protocol.value.verification.document_title_masked;
          if (protocol.value.verification.institution) form.institution = protocol.value.verification.institution;
        } else {
          if (protocol.value.author_name_masked) form.author_name_masked = protocol.value.author_name_masked;
          if (protocol.value.document_title_masked) form.document_title_masked = protocol.value.document_title_masked;
          if (protocol.value.institution) form.institution = protocol.value.institution;
        }
        if (protocol.value.section_reviews || protocol.value.sectionReviews) {
          form.section_reviews = JSON.parse(JSON.stringify(protocol.value.section_reviews || protocol.value.sectionReviews));
          form.section_reviews.forEach((sec: any) => {
            if (!sec.weightage_level) sec.weightage_level = sec.section_name || '';
            if (sec.content_pct === undefined && sec.indicator_score) {
              const num = parseFloat(sec.indicator_score);
              if (!isNaN(num)) sec.content_pct = num;
            }
          });
          describeWeightage.value = form.section_reviews.length > 0;
        } else {
          describeWeightage.value = false;
        }
      } else {
        const engine = (props.order?.ai_check_detail?.ai_engine || '').toLowerCase();
        form.engine_type = engine === 'scispace' ? 'SciSpace' : (engine === 'drillbit' ? 'Drillbit' : 'SciSpace');
        form.detector_name = form.engine_type === 'SciSpace' ? 'SciSpace AI Detection' : 'Drillbit AI Detection';
        describeWeightage.value = false;
      }
    });

    watch(() => props.order?.ai_check_detail?.ai_engine, (newEngine?: string) => {
      if (!protocol.value && newEngine) {
        form.engine_type = newEngine.toLowerCase() === 'scispace' ? 'SciSpace' : 'Drillbit';
        form.detector_name = form.engine_type === 'SciSpace' ? 'SciSpace AI Detection' : 'Drillbit AI Detection';
      }
    });

    return {
      protocol,
      signatories,
      onSignatoryChange,
      selectedSignatoryPreviewUrl,
      form,
      describeWeightage,
      onDescribeWeightageChange,
      submitting,
      generatingPdf,
      updateZone,
      applySciSpacePreset,
      onEngineTypeChange,
      addSectionReview,
      removeSectionReview,
      saveProtocol,
      generatePdf,
      getFileUrl,
      getVerificationUrl
    };
  }
});
</script>
